import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { FloatingLabelInput } from '../../../FloatingLabelInput';
import { TermsCheckbox } from '../../../../auth/TermsCheckbox';
import { OAuthButtons } from './OAuthButtons';
import { cn } from '../../../../../utils/cn';
import { isPasswordValid } from '../../../../../utils/passwordValidation';
import { AuthMode } from '../hooks/useAuthForm';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '0x4AAAAAAB9PVF5WNM2_V9pV';

interface AuthFormProps {
  // Form state
  mode: AuthMode;
  email: string;
  password: string;
  confirmPassword: string;
  username: string;
  authLoading: boolean;
  theme: 'light' | 'dark';

  // Setters
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onUsernameChange: (value: string) => void;

  // Password visibility
  showPassword: boolean;
  showConfirmPassword: boolean;
  onToggleShowPassword: () => void;
  onToggleShowConfirmPassword: () => void;

  // Email validation (for signup)
  checkingEmail: boolean;
  emailValid: boolean | null;
  emailAvailable: boolean | null;
  onCheckEmail: (email: string) => void;

  // CAPTCHA
  shouldRenderCaptcha: boolean;
  captchaResetCounter: number;
  onCaptchaSuccess: (token: string) => void;
  onCaptchaError: () => void;
  onCaptchaExpire: () => void;

  // Terms
  termsAccepted: boolean;
  termsError: string;
  onTermsChange: (checked: boolean) => void;
  onViewTerms: () => void;

  // Messages
  successMessage: string;
  hasPendingCheckout: boolean;

  // OAuth
  googleButtonLabel: string;
  appleButtonLabel: string;

  // Handlers
  onSubmit: (e: React.FormEvent) => void;
  onGoogleLogin: () => void;
  onAppleLogin: () => void;
  onModeSwitch: () => void;
  onClose: () => void;
}

export const AuthForm: React.FC<AuthFormProps> = ({
  mode,
  email,
  password,
  confirmPassword,
  username,
  authLoading,
  theme,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onUsernameChange,
  showPassword,
  showConfirmPassword,
  onToggleShowPassword,
  onToggleShowConfirmPassword,
  checkingEmail,
  emailValid,
  emailAvailable,
  onCheckEmail,
  shouldRenderCaptcha,
  captchaResetCounter,
  onCaptchaSuccess,
  onCaptchaError,
  onCaptchaExpire,
  termsAccepted,
  termsError,
  onTermsChange,
  onViewTerms,
  successMessage,
  hasPendingCheckout,
  googleButtonLabel,
  appleButtonLabel,
  onSubmit,
  onGoogleLogin,
  onAppleLogin,
  onModeSwitch,
  onClose,
}) => {
  const navigate = useNavigate();

  return (
    <div className="rounded-xl border border-[var(--auth-border-warm)] bg-[var(--auth-bg-warm)] p-6">
      <div className="flex flex-col items-center space-y-4">
        <div className="text-center space-y-4 w-full">
          {/* Checkout Intent Banner */}
          {hasPendingCheckout && (
            <div className="w-full p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-md mb-2">
              <p className="text-sm font-medium text-red-800 dark:text-red-300 text-center">
                Login or create an account to subscribe for premium.
              </p>
            </div>
          )}

          <h3 className="font-serif text-2xl text-foreground">
            {mode === 'login' ? 'Login' : 'Sign Up'}
          </h3>

          {/* OAuth Buttons */}
          <OAuthButtons
            mode={mode}
            authLoading={authLoading}
            theme={theme}
            googleButtonLabel={googleButtonLabel}
            appleButtonLabel={appleButtonLabel}
            onGoogleLogin={onGoogleLogin}
            onAppleLogin={onAppleLogin}
          />

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--auth-border-warm), transparent)' }} />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[var(--auth-bg-warm)] px-3 text-[var(--auth-text-secondary)]">or continue with email</span>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            {/* Email Field */}
            <div className="space-y-1">
              <FloatingLabelInput
                id="auth-email"
                type="email"
                label="Email"
                showAsterisk
                asteriskPosition="left"
                value={email}
                onChange={e => {
                  const val = e.target.value;
                  onEmailChange(val);
                  if (mode === 'signup') {
                    onCheckEmail(val);
                  }
                }}
                required
                autoComplete="email"
                floating
                placeholder="Email"
                className="border-[var(--auth-border-warm)] rounded-lg"
              />
              {/* Email validation feedback for signup mode */}
              {mode === 'signup' && email && (
                <div className="text-xs mt-1">
                  {checkingEmail ? (
                    <span className="text-muted-foreground">Checking email...</span>
                  ) : emailValid === false ? (
                    <span className="text-red-600">Invalid email format</span>
                  ) : emailAvailable === false ? (
                    <span className="text-red-600">Email already registered</span>
                  ) : emailValid === true && emailAvailable === true ? (
                    <span className="text-green-600">Valid email</span>
                  ) : null}
                </div>
              )}
            </div>

            {/* Username Field (signup only) */}
            {mode === 'signup' && (
              <FloatingLabelInput
                id="signup-username"
                type="text"
                label="Username"
                showAsterisk
                asteriskPosition="left"
                value={username}
                onChange={e => onUsernameChange(e.target.value)}
                required
                maxLength={20}
                autoComplete="nickname"
                className="border-[var(--auth-border-warm)] rounded-lg"
              />
            )}

            {/* Password Field */}
            <div className="space-y-1">
              <FloatingLabelInput
                id="auth-password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                showAsterisk
                asteriskPosition="left"
                value={password}
                onChange={e => onPasswordChange(e.target.value)}
                required
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                floating
                placeholder="Password"
                className="border-[var(--auth-border-warm)] rounded-lg"
                endAdornment={
                  <button
                    type="button"
                    onClick={onToggleShowPassword}
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors duration-200 ease-theme focus:outline-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                }
              />
              {mode === 'login' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/reset-password');
                      onClose();
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 ease-theme"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              {mode === 'signup' && password && (
                <p className={cn(
                  "text-xs px-1 text-left",
                  isPasswordValid(password)
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                )}>
                  6+ characters, 1 uppercase, 1 lowercase, 1 number
                </p>
              )}
            </div>

            {/* Confirm Password Field (signup only) */}
            {mode === 'signup' && (
              <>
                <FloatingLabelInput
                  id="signup-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  showAsterisk
                  asteriskPosition="left"
                  value={confirmPassword}
                  onChange={e => onConfirmPasswordChange(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="border-[var(--auth-border-warm)] rounded-lg"
                  endAdornment={
                    <button
                      type="button"
                      onClick={onToggleShowConfirmPassword}
                      className="p-1 text-muted-foreground hover:text-foreground transition-colors duration-200 ease-theme focus:outline-none"
                      aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  }
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-600 dark:text-red-400 px-1">Passwords do not match</p>
                )}

                <TermsCheckbox
                  checked={termsAccepted}
                  onChange={onTermsChange}
                  onViewTerms={onViewTerms}
                  error={termsError}
                />
              </>
            )}

            {/* CAPTCHA */}
            {shouldRenderCaptcha && (
              <div className={cn('flex justify-center', mode === 'signup' ? 'pt-2' : 'pt-1')}>
                <Turnstile
                  key={captchaResetCounter}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={onCaptchaSuccess}
                  onError={onCaptchaError}
                  onExpire={onCaptchaExpire}
                  options={{ theme: theme === 'dark' ? 'dark' : 'light' }}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={authLoading}
              className="w-full h-11 rounded-full text-white font-bold text-sm shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: `linear-gradient(135deg, var(--auth-accent-gradient-from), var(--auth-accent-gradient-to))` }}
            >
              {authLoading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {mode === 'login' ? 'Logging in...' : 'Creating account...'}
                </span>
              ) : (
                mode === 'login' ? 'Login' : 'Sign Up'
              )}
            </button>
          </form>

          {/* Success Message */}
          {successMessage && (
            <p className="text-sm text-green-600 dark:text-green-400 text-center">
              {successMessage}
            </p>
          )}

          {/* Mode Switch */}
          <p className="text-sm text-[var(--auth-text-secondary)] text-center">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={onModeSwitch}
              className="font-semibold text-[var(--auth-accent)] hover:text-[var(--auth-accent-hover)] transition-colors"
            >
              {mode === 'login' ? 'Sign up' : 'Login'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
